import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { getDisplayedVatRate } from '@features/vatRates/elements/VatRateSelect/utils/getDisplayedVatRate'
import { useGetVatRates } from '@features/vatRates/hooks/useGetVatRates'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { LocaleIdentifier } from '../../../../../../types/localeIdentifier'
import { FILTER_TYPE_META } from '../../../../constants/reviewEntriesFilterConfig'
import { ReviewEntriesFilterType } from '../../../../enums/reviewEntriesFilterType'
import type { ReviewEntriesFiltersState } from '../../../../types/reviewEntriesFilters'
import { formatReviewEntriesFilterPreview } from '../../../../utils/formatReviewEntriesFilterPreview'
import { CheckboxFilterBody } from '../../elements/CheckboxFilterBody'
import { ReviewEntriesFilterChipShell } from '../../ReviewEntriesFilterChipShell'

interface VatRateFilterChipProps {
  filters: ReviewEntriesFiltersState
  shouldAutoOpen: boolean
  onAutoOpenHandled: () => void
  onDelete: () => void
  onReset: () => void
  onChange: (values: string[]) => void
}

export const VatRateFilterChip = ({
  filters,
  shouldAutoOpen,
  onAutoOpenHandled,
  onDelete,
  onReset,
  onChange,
}: VatRateFilterChipProps): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const locale = (organization?.localeId ?? 'en_US') as LocaleIdentifier
  const { vatRates } = useGetVatRates()
  const selectedValues = filters.vatRate ?? []
  const options = useMemo(
    () =>
      vatRates.map((vatRate) =>
        getDisplayedVatRate({
          locale,
          vatRate,
        }),
      ),
    [locale, vatRates],
  )
  const label = t(FILTER_TYPE_META[ReviewEntriesFilterType.VatRate].labelKey)

  return (
    <ReviewEntriesFilterChipShell
      isActive={selectedValues.length > 0}
      label={label}
      onAutoOpenHandled={onAutoOpenHandled}
      onDelete={onDelete}
      onReset={onReset}
      shouldAutoOpen={shouldAutoOpen}
      type={ReviewEntriesFilterType.VatRate}
      valuePreview={formatReviewEntriesFilterPreview(ReviewEntriesFilterType.VatRate, filters)}
    >
      <CheckboxFilterBody onChange={onChange} options={options} selectedValues={selectedValues} />
    </ReviewEntriesFilterChipShell>
  )
}
